import { resolvedPromiseLike } from '@conduit-client/utils';
import { setHeader } from './utils';
import type { JwtManager, JwtToken } from '@conduit-client/jwt-manager';
import type { FetchParameters } from './fetch';

const UNEXPECTED_AUTHORIZATION_HEADER_MESSAGE =
    'Unexpected Authorization header encountered. To specify a custom Authorization header, use a Fetch service that is not configured with JwtRequestHeaderInterceptor';

/**
 * Sets the Authorization header with a JWT token using the generic setHeader utility
 * @param token - The JWT token to set in the Authorization header
 * @param fetchParams - The fetch parameters [resource, options]
 * @returns Modified fetch parameters with the Authorization header set
 */
export function setHeaderAuthorization<T, E>(
    { token }: JwtToken<T, E>,
    fetchParams: FetchParameters
): FetchParameters {
    const authorizationValue = `Bearer ${token}`;

    return setHeader('Authorization', authorizationValue, fetchParams, {
        throwOnExisting: true,
        errorMessage: UNEXPECTED_AUTHORIZATION_HEADER_MESSAGE,
    });
}

export type JwtRequestModifier<ExtraInfo = unknown> = (
    extraInfo: ExtraInfo,
    fetchArgs: FetchParameters
) => FetchParameters;

export function buildJwtRequestHeaderInterceptor<ExtraInfo>(
    jwtManager: JwtManager<unknown, ExtraInfo>,
    jwtRequestModifier: JwtRequestModifier<ExtraInfo> = (_e, fetchArgs) => fetchArgs
) {
    return (args: FetchParameters) => {
        return resolvedPromiseLike(jwtManager.getJwt()).then((token) => {
            const fetchArgsWithRequestHeaderAuthorization = setHeaderAuthorization(token, args);
            return token.extraInfo
                ? jwtRequestModifier(token.extraInfo, fetchArgsWithRequestHeaderAuthorization)
                : fetchArgsWithRequestHeaderAuthorization;
        });
    };
}
